import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { config } from '../config';

export interface CustomerOnboardingRequest {
  customerName: string;
  regionName?: string;
  adminEmail?: string;
  adminFirstName?: string;
  adminLastName?: string;
}

export interface DivisionInfo {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface RegionInfo {
  id: string;
  name: string;
  slug: string;
  divisionId: string | null;
  createdAt: string;
}

export interface ContextInfo {
  id: string;
  name: string;
  divisionId: string | null;
  createdAt: string;
}

export interface TeamInfo {
  id: string;
  name: string;
  divisionId: string | null;
  createdAt: string;
}

export interface ProvisioningTokenInfo {
  id: string;
  token: string;
  name: string;
  regionId: string;
  divisionId: string | null;
  createdAt: string;
}

export interface InvitationInfo {
  id: string;
  email: string;
  token: string;
  teamId: string;
  contextId: string | null;
  expiresAt: string;
}

export interface CustomerOnboardingResponse {
  division: DivisionInfo;
  region: RegionInfo;
  context: ContextInfo;
  team: TeamInfo;
  provisioningToken: ProvisioningTokenInfo;
  invitation?: InvitationInfo;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

const backendApi = config.backendApi;

export function useMSPOnboarding() {
  const queryClient = useQueryClient();

  return useMutation<CustomerOnboardingResponse, AxiosError<ApiErrorResponse>, CustomerOnboardingRequest>({
    mutationFn: async (request) => {
      const response = await axios.post<CustomerOnboardingResponse>(
        `${backendApi}/api/msp/onboarding/customer`,
        request,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['msp-divisions'] });
      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
      void queryClient.invalidateQueries({ queryKey: ['regions'] });
    },
  });
}
